/**
 * Page publique du Live School — /live/<niveau>.
 *
 * Non connecté → inscription/connexion OTP inline, AVEC le niveau pré-défini
 * (`?niveau=` transmis à l'onboarding pour un nouveau compte).
 * Connecté élève de CE niveau → joue (classé). Élève d'un AUTRE niveau → bloqué
 * (un élève ne joue que le live de SON niveau ; la vraie garde est serveur).
 * Prof, curieux, parent → jouent n'importe quel niveau SANS classement.
 * Pas encore de profil → finalise l'inscription. `?defi=<id>` = un live précis
 * (Live School du dimanche : plusieurs lives par niveau).
 */

import { useQuery } from '@tanstack/react-query';
import { SCHOOL_LEVELS, SECONDARY_SCHOOL_LEVELS, type SchoolLevel } from '@jeraisonne/db';
import { Button, CardTitle, CardDescription, CompassLogo } from '@jeraisonne/ui';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { useAuth } from '@/lib/auth';
import { isLiveTester } from '@/lib/live-tester';
import { supabase } from '@/lib/supabase';
import { CountdownDigits, LiveQuizScreen } from '@/features/live-quiz/live-quiz-screen';
import { signupCountdownView, useLiveCountdown } from '@/features/live-quiz/use-live-countdown';
import { liveCoverUrl, presetPath } from '@/lib/live-covers';
import { ArenaPanel, LiveArena } from '@/features/live-ui/live-arena';

const VALID_LEVELS = new Set(SECONDARY_SCHOOL_LEVELS.map((l) => l.value));

function getNiveau(): string {
  return decodeURIComponent(window.location.pathname.split('/').filter(Boolean).pop() ?? '');
}

function levelLabel(level: string): string {
  return SCHOOL_LEVELS.find((l) => l.value === level)?.label ?? level;
}

export function LiveQuizPage() {
  const niveau = getNiveau();
  const { session, profile, loading } = useAuth();
  const userId = session?.user.id ?? null;

  const levelQuery = useQuery({
    queryKey: ['student-profile-level', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from('student_profiles')
        .select('level')
        .eq('id', userId!)
        .maybeSingle();
      return (data?.level as SchoolLevel | null) ?? null;
    },
  });

  if (!VALID_LEVELS.has(niveau as SchoolLevel)) {
    return (
      <Shell>
        <CardTitle>Niveau inconnu</CardTitle>
        <CardDescription>
          Ce niveau n&apos;existe pas. Reviens à l&apos;accueil du défi.
        </CardDescription>
      </Shell>
    );
  }

  if (loading) {
    return <Shell>Chargement…</Shell>;
  }

  // Non connecté → inscription/connexion (niveau pré-défini pour l'inscription).
  // On affiche quand même le compte à rebours du prochain défi (live-state est public).
  if (!session) {
    return (
      <Shell>
        <OtpAuthForm
          initialMode="signup"
          renderHeader={({ step, email }) =>
            step === 'otp' ? (
              <>
                <CardTitle>Vérifie ta boîte mail</CardTitle>
                <CardDescription>
                  Entre le code à 6 chiffres envoyé à{' '}
                  <strong className="text-ink-700">{email}</strong>.
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle>Rejoindre le Live School · {levelLabel(niveau)}</CardTitle>
                <LiveSignupCountdown niveau={niveau} />
              </>
            )
          }
        />
      </Shell>
    );
  }

  // Mode TEST (staff/dev) : `?test=<challengeId>` rejoue le vrai player à la
  // demande, en court-circuitant les gardes rôle/niveau (la vraie garde est
  // serveur : `live-test`/`live-key`/`submit` exigent un email @jeraisonne.fr).
  const testChallengeId = new URLSearchParams(window.location.search).get('test');
  if (testChallengeId && isLiveTester(profile, session.user.email)) {
    return <LiveQuizScreen niveau={niveau} userId={userId!} testChallengeId={testChallengeId} />;
  }

  const defiParam = new URLSearchParams(window.location.search).get('defi');
  const defiId = defiParam && /^[0-9a-f-]{36}$/i.test(defiParam) ? defiParam : undefined;

  // Connecté mais pas encore de profil → finaliser l'inscription au bon niveau.
  if (!profile) {
    return (
      <Shell>
        <CardTitle>Termine ton inscription</CardTitle>
        <CardDescription className="mb-4">
          Encore une étape pour jouer le Live School niveau {levelLabel(niveau)}.
        </CardDescription>
        <Button fullWidth onClick={() => (window.location.href = `/onboarding?niveau=${niveau}`)}>
          Continuer
        </Button>
      </Shell>
    );
  }

  // Prof, curieux, parent : jouent sans classement (garde serveur `liveAccess`).
  if (profile.role !== 'student') {
    return <LiveQuizScreen niveau={niveau} userId={userId!} challengeId={defiId} />;
  }

  if (levelQuery.isLoading) {
    return <Shell>Chargement…</Shell>;
  }

  const level = levelQuery.data ?? null;

  if (!level) {
    return (
      <Shell>
        <CardTitle>Choisis ton niveau</CardTitle>
        <CardDescription className="mb-4">
          Renseigne ton niveau scolaire pour participer au Live School.
        </CardDescription>
        <Button fullWidth onClick={() => (window.location.href = `/onboarding?niveau=${niveau}`)}>
          Définir mon niveau
        </Button>
      </Shell>
    );
  }

  // Garde « son niveau uniquement » (UX ; la vraie garde est serveur).
  if (level !== niveau) {
    return (
      <Shell>
        <CardTitle>Le défi se joue à ton niveau</CardTitle>
        <CardDescription className="mb-4">
          Ton niveau est {levelLabel(level)}. Rejoins le Live School de ton niveau.
        </CardDescription>
        <Button fullWidth onClick={() => (window.location.href = `/live/${level}`)}>
          Aller au défi {levelLabel(level)}
        </Button>
      </Shell>
    );
  }

  return <LiveQuizScreen niveau={niveau} userId={userId!} challengeId={defiId} />;
}

/**
 * En-tête de l'écran d'inscription : compte à rebours du prochain défi quand il
 * n'a pas encore démarré, sinon un message d'incitation. `live-state` est public,
 * donc on connaît l'heure de départ même sans compte.
 */
function LiveSignupCountdown({ niveau }: { niveau: string }) {
  const { status, timeLeftMs } = useLiveCountdown(niveau);

  if (signupCountdownView(status) === 'countdown') {
    return (
      <div className="space-y-3">
        <p className="text-ink-600 text-sm">Le prochain défi commence dans</p>
        <CountdownDigits timeLeftMs={timeLeftMs} size="md" />
        <CardDescription>Inscris-toi maintenant pour être prêt au top départ.</CardDescription>
      </div>
    );
  }

  if (signupCountdownView(status) === 'live') {
    return (
      <CardDescription>
        Le défi est en cours&nbsp;! Inscris-toi vite pour le rejoindre.
      </CardDescription>
    );
  }

  return (
    <CardDescription>
      Inscris-toi pour jouer en direct avec toute la France de ton niveau.
    </CardDescription>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <LiveArena coverUrl={liveCoverUrl({ path: presetPath('arene') })}>
      <div className="native:pt-[env(safe-area-inset-top)]">
        <header className="flex justify-center px-6 py-6">
          <CompassLogo size="md" wordmarkClassName="text-white" />
        </header>
        <main className="mx-auto flex w-full max-w-md flex-col px-4 pb-[max(4rem,env(safe-area-inset-bottom))]">
          <ArenaPanel className="space-y-3 py-6 text-center sm:p-6">{children}</ArenaPanel>
        </main>
      </div>
    </LiveArena>
  );
}
